import {
  patientPortalDefaultFeatureFlagsV1,
  patientWorkspaceDocumentCommandContractVersion,
  patientWorkspaceMessageCommandContractVersion,
  patientWorkspaceOnboardingCommandContractVersion,
  patientWorkspaceSummaryContractVersion,
  supportSubmissionContractVersion,
  type ClinicianWorkbenchApprovalSummary,
  type ClinicianWorkbenchConversation,
  type ClinicianWorkbenchConversationMessage,
  type ClinicianWorkbenchDeliverySummary,
  type ClinicianWorkbenchDetailSection,
  type ClinicianWorkbenchDocumentSummary,
  type ClinicianWorkbenchTaskAssignmentSummary,
  type PatientWorkspaceDocumentCommandResponseV1,
  type PatientWorkspaceEntryPointRouteV1,
  type PatientWorkspaceMessageCommandResponseV1,
  type PatientWorkspaceOnboardingCommandRequestV1,
  type PatientWorkspaceOnboardingCommandResponseV1,
  type PatientWorkspaceOnboardingStateV1,
  type PatientWorkspaceSummaryResponseV1,
  type SupportSubmissionRequestV1,
  type SupportSubmissionResponseV1,
} from "@amplecare/contracts";

import type { MobileRuntimeConfig } from "../config/runtime-config.js";

const defaultPatientId = "patient-preview-olivia-hale";
const defaultPhoneNumber = "0400123456";
const scaffoldChallengeCode = "246810";
const scaffoldChallengeWindowMs = 10 * 60 * 1000;

let scaffoldSequence = 0;

const scaffoldSummaries = new Map<string, PatientWorkspaceSummaryResponseV1>();

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function nextScaffoldId(prefix: string): string {
  scaffoldSequence += 1;

  return `${prefix}-${scaffoldSequence}`;
}

function nowIso(offsetMs = 0): string {
  return new Date(Date.now() + offsetMs).toISOString();
}

function resolvePhoneNumberLast4(
  phoneNumber: string | null | undefined,
): string | null {
  const digitsOnly = (phoneNumber ?? "").replace(/\D+/gu, "");

  return digitsOnly.length >= 4 ? digitsOnly.slice(-4) : null;
}

function normalizePatientId(patientId: string | null | undefined): string {
  return patientId?.trim().length ? patientId : defaultPatientId;
}

function createConversationMessage(input: {
  bodyText: string;
  senderDisplayName: string;
  senderKind: ClinicianWorkbenchConversationMessage["senderKind"];
  sentAt: string;
  deliveryState: ClinicianWorkbenchConversationMessage["deliveryState"];
}): ClinicianWorkbenchConversationMessage {
  return {
    messageId: nextScaffoldId("patient-workspace-message"),
    senderKind: input.senderKind,
    senderDisplayName: input.senderDisplayName,
    bodyText: input.bodyText,
    sentAt: input.sentAt,
    deliveryState: input.deliveryState,
    review: null,
  };
}

function buildScaffoldConversation(): ClinicianWorkbenchConversation {
  return {
    conversationId: "patient-workspace-preview-conversation",
    messages: [
      createConversationMessage({
        bodyText:
          "Welcome back. Your care plan, records, and next steps stay updated here.",
        senderDisplayName: "AmpleCare",
        senderKind: "system",
        sentAt: nowIso(-1000 * 60 * 60 * 36),
        deliveryState: "system",
      }),
      createConversationMessage({
        bodyText:
          "Please review your referral and consent packet before your procedure review.",
        senderDisplayName: "Maya Chen, RN",
        senderKind: "clinician",
        sentAt: nowIso(-1000 * 60 * 60 * 18),
        deliveryState: "sent",
      }),
      createConversationMessage({
        bodyText:
          "I have the consent packet ready and will bring the referral tonight.",
        senderDisplayName: "Olivia Hale",
        senderKind: "patient",
        sentAt: nowIso(-1000 * 60 * 60 * 6),
        deliveryState: "delivered",
      }),
    ],
  };
}

function buildScaffoldTaskAssignments(): ClinicianWorkbenchTaskAssignmentSummary[] {
  return [
    {
      assignmentId: "patient-workspace-task-intake",
      taskLabel: "Finish intake checklist",
      targetKind: "queue",
      targetLabel: "Pre-op intake",
      statusLabel: "Ready",
      canComplete: false,
    },
  ];
}

function buildScaffoldApprovals(): ClinicianWorkbenchApprovalSummary[] {
  return [
    {
      approvalId: "patient-workspace-approval-consent",
      label: "Consent packet",
      ownerLabel: "Pre-op nurse",
      statusLabel: "Pending review",
      updatedAtText: "Updated today",
    },
  ];
}

function buildScaffoldDeliveries(): ClinicianWorkbenchDeliverySummary[] {
  return [
    {
      deliveryId: "patient-workspace-delivery-reminder",
      channelLabel: "Portal reminder",
      recipientLabel: "Olivia Hale",
      statusLabel: "Delivered",
      updatedAtText: "Sent this morning",
    },
  ];
}

function buildScaffoldDocuments(): ClinicianWorkbenchDocumentSummary[] {
  return [
    {
      documentId: "patient-workspace-document-consent",
      label: "Consent packet",
      summaryText: "Procedure consent packet received for review.",
      statusLabel: "Pending review",
      createdAt: nowIso(-1000 * 60 * 60 * 28),
      createdByLabel: "Patient provided",
      availability: "download-ready",
      canMarkReviewed: false,
      countsTowardPendingDocumentCount: true,
    },
    {
      documentId: "patient-workspace-document-insurance",
      label: "Insurance card",
      summaryText: "Insurance card verified for the upcoming visit.",
      statusLabel: "Received",
      createdAt: nowIso(-1000 * 60 * 60 * 52),
      createdByLabel: "Front desk",
      availability: "download-ready",
      canMarkReviewed: false,
      countsTowardPendingDocumentCount: false,
    },
  ];
}

function buildScaffoldDetailSections(
  patient: PatientWorkspaceSummaryResponseV1["patient"],
): ClinicianWorkbenchDetailSection[] {
  return [
    {
      id: "information",
      title: "Patient information",
      summaryText: null,
      rows: [
        {
          id: "patient-workspace-information-coordinator",
          label: "Care coordinator",
          valueText: patient.coordinatorLabel,
        },
        {
          id: "patient-workspace-information-program",
          label: "Program",
          valueText: patient.programLabel,
        },
        {
          id: "patient-workspace-information-contact-window",
          label: "Contact window",
          valueText: patient.contactWindowText,
        },
      ],
    },
    {
      id: "case-summary",
      title: "Care summary",
      summaryText: patient.summaryText,
      rows: [
        ...patient.taskAssignments.map((assignment) => ({
          id: `patient-workspace-task-${assignment.assignmentId}`,
          label: assignment.taskLabel,
          valueText: `${assignment.targetLabel} | ${assignment.statusLabel}`,
        })),
        ...patient.approvalSummaries.map((approval) => ({
          id: `patient-workspace-approval-${approval.approvalId}`,
          label: approval.label,
          valueText: `${approval.ownerLabel} | ${approval.statusLabel}`,
        })),
        ...patient.deliverySummaries.map((delivery) => ({
          id: `patient-workspace-delivery-${delivery.deliveryId}`,
          label: delivery.channelLabel,
          valueText: `${delivery.recipientLabel} | ${delivery.statusLabel}`,
        })),
      ],
    },
    {
      id: "appointments",
      title: "Appointments",
      summaryText: null,
      rows: [
        {
          id: "patient-workspace-appointment-next",
          label: "Next visit",
          valueText: patient.nextAppointmentText,
        },
        {
          id: "patient-workspace-appointment-window",
          label: "Check-in window",
          valueText: patient.contactWindowText,
        },
      ],
    },
    {
      id: "procedures",
      title: "Procedures",
      summaryText: null,
      rows: [
        {
          id: "patient-workspace-procedure-plan",
          label: "Procedure plan",
          valueText: patient.procedureSummaryText,
        },
      ],
    },
    {
      id: "medications",
      title: "Medications",
      summaryText: null,
      rows: [
        {
          id: "patient-workspace-medication-plan",
          label: "Medication note",
          valueText: patient.medicationSummaryText,
        },
      ],
    },
    {
      id: "documents",
      title: "Documents",
      summaryText: null,
      rows: patient.documentSummaries.map((document) => ({
        id: `patient-workspace-document-${document.documentId}`,
        label: document.label,
        valueText: `${document.summaryText} (${document.statusLabel})`,
      })),
    },
  ];
}

function buildScaffoldOnboardingState(): PatientWorkspaceOnboardingStateV1 {
  return {
    initialFlowComplete: false,
    completedAt: null,
    entryPointRoute: "overview",
    auth: {
      status: "not-started",
      method: null,
      verifiedAt: null,
      phoneNumberLast4: resolvePhoneNumberLast4(defaultPhoneNumber),
      challengeIssuedAt: null,
      challengeExpiresAt: null,
      attemptsRemaining: null,
      deliveryMode: null,
      smsDispatchEnabled: false,
    },
    preferences: {
      remindersEnabled: true,
      reminderChannel: "portal",
      wakeUpTime: "07:00",
      updatedAt: nowIso(-1000 * 60 * 60 * 24),
    },
  };
}

function resolveScaffoldStatusLabel(
  patient: PatientWorkspaceSummaryResponseV1["patient"],
): string {
  if (patient.onboarding.auth.status !== "verified") {
    return "Verify portal access";
  }

  if (!patient.onboarding.initialFlowComplete) {
    return "Finish onboarding";
  }

  if (patient.pendingDocumentCount > 0) {
    return "Review remaining records";
  }

  return "Ready for care";
}

function refreshScaffoldSummary(
  summary: PatientWorkspaceSummaryResponseV1,
): PatientWorkspaceSummaryResponseV1 {
  summary.generatedAt = nowIso();
  summary.projectionVersion = nextScaffoldId("patient-workspace-summary");
  summary.patient.pendingDocumentCount = summary.patient.documentSummaries.filter(
    (document) => document.countsTowardPendingDocumentCount,
  ).length;
  summary.patient.requiresAttention =
    summary.patient.pendingDocumentCount > 0 ||
    summary.patient.unreadMessageCount > 0 ||
    summary.patient.onboarding.auth.status !== "verified" ||
    !summary.patient.onboarding.initialFlowComplete;
  summary.patient.statusLabel = resolveScaffoldStatusLabel(summary.patient);
  summary.patient.materialChangeToken = summary.projectionVersion;
  summary.patient.detailSections = buildScaffoldDetailSections(summary.patient);

  return summary;
}

function createBaseSummary(
  tenantId: string,
  patientId: string,
): PatientWorkspaceSummaryResponseV1 {
  const patient: PatientWorkspaceSummaryResponseV1["patient"] = {
    patientId,
    displayName: "Olivia Hale",
    identifier: "MRN 104829",
    legalName: "Olivia Hale",
    dateOfBirth: "1986-02-11",
    medicalRecordNumber: "104829",
    summaryText:
      "Your pre-op checklist, records, and coordinator updates are grouped into one place.",
    statusLabel: "Verify portal access",
    derivedFlagKeys: ["pre-op"],
    coordinatorLabel: "Maya Chen, RN",
    programLabel: "Orthopedics",
    nextAppointmentText: "Tue 21 Apr at 9:30 AM",
    materialChangeToken: nextScaffoldId("patient-workspace-material-change"),
    contactWindowText: "Weekdays 8:00 AM to 4:00 PM",
    procedureSummaryText:
      "Left knee arthroscopy preparation with fasting and arrival instructions.",
    medicationSummaryText:
      "Pause ibuprofen 48 hours before the procedure and continue water with morning meds.",
    unreadMessageCount: 1,
    pendingDocumentCount: 1,
    requiresAttention: true,
    conversation: buildScaffoldConversation(),
    taskAssignments: buildScaffoldTaskAssignments(),
    approvalSummaries: buildScaffoldApprovals(),
    deliverySummaries: buildScaffoldDeliveries(),
    documentSummaries: buildScaffoldDocuments(),
    voiceCallSummaries: [],
    detailSections: [],
    clinicalNotes: [],
    onboarding: buildScaffoldOnboardingState(),
  };
  const summary: PatientWorkspaceSummaryResponseV1 = {
    schemaVersion: patientWorkspaceSummaryContractVersion,
    tenantId,
    patientId,
    projectionVersion: nextScaffoldId("patient-workspace-projection"),
    generatedAt: nowIso(),
    featureFlags: patientPortalDefaultFeatureFlagsV1,
    patient,
  };

  return refreshScaffoldSummary(summary);
}

function buildScaffoldSummaryKey(
  config: MobileRuntimeConfig,
  patientId: string | null | undefined,
): string {
  return `${config.requestTenantId}:${normalizePatientId(patientId)}`;
}

function getOrCreateScaffoldSummary(
  config: MobileRuntimeConfig,
  patientId: string | null | undefined,
): PatientWorkspaceSummaryResponseV1 {
  const normalizedPatientId = normalizePatientId(patientId);
  const summaryKey = buildScaffoldSummaryKey(config, normalizedPatientId);
  const existingSummary = scaffoldSummaries.get(summaryKey);

  if (existingSummary) {
    return existingSummary;
  }

  const createdSummary = createBaseSummary(
    config.requestTenantId,
    normalizedPatientId,
  );

  scaffoldSummaries.set(summaryKey, createdSummary);

  return createdSummary;
}

function buildAuthChallengeResponse(input: {
  action: "request" | "verify";
  attemptsRemaining: number | null;
  developmentCode?: string | null;
  expiresAt: string | null;
  phoneNumberLast4: string | null;
  status:
    | "challenge-issued"
    | "verified"
    | "failed"
    | "expired"
    | "locked";
}) {
  return {
    action: input.action,
    status: input.status,
    phoneNumberLast4: input.phoneNumberLast4,
    expiresAt: input.expiresAt,
    attemptsRemaining: input.attemptsRemaining,
    smsDispatchEnabled: false as const,
    deliveryMode: "portal" as const,
    ...(input.developmentCode ? { developmentCode: input.developmentCode } : {}),
  };
}

function updateEntryPointRoute(
  summary: PatientWorkspaceSummaryResponseV1,
  entryPointRoute: PatientWorkspaceEntryPointRouteV1 | null | undefined,
): void {
  if (entryPointRoute) {
    summary.patient.onboarding.entryPointRoute = entryPointRoute;
  }
}

function resolveFailedChallengeResponse(
  summary: PatientWorkspaceSummaryResponseV1,
  status: "failed" | "expired" | "locked",
) {
  const auth = summary.patient.onboarding.auth;

  return buildAuthChallengeResponse({
    action: "verify",
    attemptsRemaining: auth.attemptsRemaining,
    expiresAt: auth.challengeExpiresAt,
    phoneNumberLast4: auth.phoneNumberLast4,
    status,
  });
}

function recordAuthChallenge(
  summary: PatientWorkspaceSummaryResponseV1,
  input: NonNullable<PatientWorkspaceOnboardingCommandRequestV1["authChallenge"]>,
) {
  if (input.action === "request") {
    const issuedAt = nowIso();
    const expiresAt = nowIso(scaffoldChallengeWindowMs);
    const phoneNumberLast4 =
      resolvePhoneNumberLast4(input.phoneNumber) ??
      summary.patient.onboarding.auth.phoneNumberLast4;

    summary.patient.onboarding.auth = {
      status: "challenge-issued",
      method: "test-otp",
      verifiedAt: null,
      phoneNumberLast4,
      challengeIssuedAt: issuedAt,
      challengeExpiresAt: expiresAt,
      attemptsRemaining: 3,
      deliveryMode: "portal",
      smsDispatchEnabled: false,
    };

    return buildAuthChallengeResponse({
      action: "request",
      attemptsRemaining: 3,
      developmentCode: scaffoldChallengeCode,
      expiresAt,
      phoneNumberLast4,
      status: "challenge-issued",
    });
  }

  const auth = summary.patient.onboarding.auth;

  if (
    auth.challengeExpiresAt &&
    new Date(auth.challengeExpiresAt).getTime() < Date.now()
  ) {
    auth.status = "not-started";
    auth.challengeIssuedAt = null;
    auth.challengeExpiresAt = null;

    return resolveFailedChallengeResponse(summary, "expired");
  }

  if ((input.otpCode ?? "").trim() === scaffoldChallengeCode) {
    summary.patient.onboarding.auth = {
      status: "verified",
      method: "test-otp",
      verifiedAt: nowIso(),
      phoneNumberLast4:
        resolvePhoneNumberLast4(input.phoneNumber) ?? auth.phoneNumberLast4,
      challengeIssuedAt: null,
      challengeExpiresAt: null,
      attemptsRemaining: null,
      deliveryMode: "portal",
      smsDispatchEnabled: false,
    };

    return buildAuthChallengeResponse({
      action: "verify",
      attemptsRemaining: null,
      expiresAt: null,
      phoneNumberLast4: summary.patient.onboarding.auth.phoneNumberLast4,
      status: "verified",
    });
  }

  const remainingAttempts = Math.max((auth.attemptsRemaining ?? 3) - 1, 0);

  summary.patient.onboarding.auth.attemptsRemaining = remainingAttempts;

  if (remainingAttempts === 0) {
    summary.patient.onboarding.auth.status = "locked";

    return resolveFailedChallengeResponse(summary, "locked");
  }

  summary.patient.onboarding.auth.status = "challenge-issued";

  return resolveFailedChallengeResponse(summary, "failed");
}

function applyOnboardingUpdates(
  summary: PatientWorkspaceSummaryResponseV1,
  input: Omit<
    PatientWorkspaceOnboardingCommandRequestV1,
    "schemaVersion" | "tenantId"
  >,
) {
  let authChallenge: PatientWorkspaceOnboardingCommandResponseV1["authChallenge"] =
    null;

  if (input.authAttestation?.method === "portal-session") {
    summary.patient.onboarding.auth = {
      status: "verified",
      method: "portal-session",
      verifiedAt: nowIso(),
      phoneNumberLast4: summary.patient.onboarding.auth.phoneNumberLast4,
      challengeIssuedAt: null,
      challengeExpiresAt: null,
      attemptsRemaining: null,
      deliveryMode: "portal",
      smsDispatchEnabled: false,
    };
  } else if (input.authAttestation?.method === "test-otp") {
    summary.patient.onboarding.auth = {
      status: "verified",
      method: "test-otp",
      verifiedAt: nowIso(),
      phoneNumberLast4:
        resolvePhoneNumberLast4(input.authAttestation.phoneNumber) ??
        summary.patient.onboarding.auth.phoneNumberLast4,
      challengeIssuedAt: null,
      challengeExpiresAt: null,
      attemptsRemaining: null,
      deliveryMode: "portal",
      smsDispatchEnabled: false,
    };
  }

  if (input.authChallenge) {
    authChallenge = recordAuthChallenge(summary, input.authChallenge);
  }

  if (typeof input.initialFlowComplete === "boolean") {
    summary.patient.onboarding.initialFlowComplete = input.initialFlowComplete;
    summary.patient.onboarding.completedAt = input.initialFlowComplete
      ? nowIso()
      : null;
  }

  updateEntryPointRoute(summary, input.entryPointRoute);

  if (typeof input.remindersEnabled === "boolean") {
    summary.patient.onboarding.preferences.remindersEnabled =
      input.remindersEnabled;
    summary.patient.onboarding.preferences.updatedAt = nowIso();
  }

  if (input.wakeUpTime !== undefined) {
    summary.patient.onboarding.preferences.wakeUpTime = input.wakeUpTime;
    summary.patient.onboarding.preferences.updatedAt = nowIso();
  }

  return authChallenge;
}

export function isPatientWorkspaceScaffoldMode(
  config: MobileRuntimeConfig,
): boolean {
  return config.tenantSessionBootstrapMode === "scaffold-header";
}

export async function loadPatientWorkspaceScaffoldSummary(
  config: MobileRuntimeConfig,
  patientId: string | null,
): Promise<PatientWorkspaceSummaryResponseV1> {
  const summary = refreshScaffoldSummary(
    getOrCreateScaffoldSummary(config, patientId),
  );

  return cloneValue(summary);
}

export async function recordPatientWorkspaceScaffoldDocument(
  config: MobileRuntimeConfig,
  input: {
    patientId: string | null;
    label: string;
    summaryText: string;
    createdByLabel: string;
  },
): Promise<PatientWorkspaceDocumentCommandResponseV1> {
  const summary = getOrCreateScaffoldSummary(config, input.patientId);
  const pendingDocument = summary.patient.documentSummaries.find(
    (document) => document.countsTowardPendingDocumentCount,
  );

  if (pendingDocument) {
    pendingDocument.countsTowardPendingDocumentCount = false;
    pendingDocument.statusLabel = "Received";
  }

  const document: ClinicianWorkbenchDocumentSummary = {
    documentId: nextScaffoldId("patient-workspace-document"),
    label: input.label.trim() || "Added document",
    summaryText: input.summaryText.trim() || "Added from the patient workspace.",
    statusLabel: "Added",
    createdAt: nowIso(),
    createdByLabel: input.createdByLabel.trim() || "Patient provided",
    availability: "download-ready",
    canMarkReviewed: false,
    countsTowardPendingDocumentCount: false,
  };

  summary.patient.documentSummaries = [
    document,
    ...summary.patient.documentSummaries,
  ];

  refreshScaffoldSummary(summary);

  return {
    schemaVersion: patientWorkspaceDocumentCommandContractVersion,
    tenantId: config.requestTenantId,
    patientId: summary.patientId,
    document: cloneValue(document),
    summary: cloneValue(summary),
  };
}

export async function recordPatientWorkspaceScaffoldMessage(
  config: MobileRuntimeConfig,
  input: {
    patientId: string | null;
    bodyText: string;
  },
): Promise<PatientWorkspaceMessageCommandResponseV1> {
  const summary = getOrCreateScaffoldSummary(config, input.patientId);
  const patientMessage = createConversationMessage({
    bodyText: input.bodyText.trim() || "Patient message sent from the preview workspace.",
    senderDisplayName: summary.patient.displayName,
    senderKind: "patient",
    sentAt: nowIso(),
    deliveryState: "delivered",
  });
  const agentDraftMessage = createConversationMessage({
    bodyText:
      "Thanks, we received your message. Your coordinator will follow up here shortly.",
    senderDisplayName: "AmpleCare",
    senderKind: "system",
    sentAt: nowIso(1000 * 60),
    deliveryState: "system",
  });

  summary.patient.conversation.messages = [
    ...summary.patient.conversation.messages,
    patientMessage,
    agentDraftMessage,
  ];
  summary.patient.unreadMessageCount += 1;

  refreshScaffoldSummary(summary);

  return {
    schemaVersion: patientWorkspaceMessageCommandContractVersion,
    tenantId: config.requestTenantId,
    patientId: summary.patientId,
    patientMessage: cloneValue(patientMessage),
    agentDraftMessage: cloneValue(agentDraftMessage),
    summary: cloneValue(summary),
  };
}

export async function recordPatientWorkspaceScaffoldOnboarding(
  config: MobileRuntimeConfig,
  input: Omit<
    PatientWorkspaceOnboardingCommandRequestV1,
    "schemaVersion" | "tenantId"
  >,
): Promise<PatientWorkspaceOnboardingCommandResponseV1> {
  const summary = getOrCreateScaffoldSummary(config, input.patientId);
  const authChallenge = applyOnboardingUpdates(summary, input);

  refreshScaffoldSummary(summary);

  return {
    schemaVersion: patientWorkspaceOnboardingCommandContractVersion,
    tenantId: config.requestTenantId,
    patientId: summary.patientId,
    onboarding: cloneValue(summary.patient.onboarding),
    authChallenge,
    summary: cloneValue(summary),
  };
}

export async function submitPatientWorkspaceScaffoldSupportSubmission(
  config: MobileRuntimeConfig,
  input: Omit<SupportSubmissionRequestV1, "schemaVersion" | "tenantId">,
): Promise<SupportSubmissionResponseV1> {
  return {
    schemaVersion: supportSubmissionContractVersion,
    tenantId: config.requestTenantId,
    submissionId: nextScaffoldId("patient-workspace-support"),
    status: "accepted",
    receivedAt: nowIso(),
    storageLabel: "control-plane-preview-log",
    summaryText:
      input.submissionKind === "bug-report"
        ? "Bug report saved for follow-up."
        : "Feedback saved for the preview workspace.",
  };
}

export async function synthesizePatientWorkspaceScaffoldSpeech(): Promise<{
  audioBlob: Blob;
  engine: null;
  voiceId: null;
}> {
  return {
    audioBlob: new Blob([], { type: "audio/mpeg" }),
    engine: null,
    voiceId: null,
  };
}
